import { createSlice, createSelector } from "@reduxjs/toolkit";
import vendors from "constants/vendors";

export const LOADING_STATES = {
  IDLE_STATUS: "idle",
  LOADING_STATUS: "loading",
  SUCCESS_STATUS: "success",
  FAILURE_STATUS: "failure",
};

export const isMatchingLineItem = (cartItem, lineItem, vendorCode) => {
  if (!lineItem.style_data_key) return false;

  const dgiStyle = lineItem.style_data_key.replace(/\D/g, "");
  const dgiColor = lineItem.style_data_key.replace(/\d+/g, "");

  const hasSameDGIStyleSku = cartItem.dgi_style_sku === dgiStyle;
  const hasSameColor =
    (cartItem.master_color || "").trim().toLowerCase() ===
    (dgiColor || "").trim().toLowerCase();
  const hasSameSize =
    (cartItem.size || "").trim().toLowerCase() ===
    (lineItem.sizeLabel || "").trim().toLowerCase();
  const hasSameVendor = vendorCode === lineItem.distributor;

  const hasSameWarehouseCode =
    cartItem.warehouseCode &&
    lineItem.warehouseCode &&
    cartItem.warehouseCode === lineItem.warehouseCode;

  const hasSameWarehouseName =
    cartItem.warehouse &&
    lineItem.warehouse &&
    (cartItem.warehouse || "").trim().replace(" ", "").toLowerCase() ===
      (lineItem.warehouse || "").trim().replace(" ", "").toLowerCase();

  return (
    hasSameDGIStyleSku &&
    hasSameColor &&
    hasSameSize &&
    hasSameVendor &&
    (hasSameWarehouseCode || hasSameWarehouseName)
  );
};

export const tagCartItemsWithInvoiceIDs = (
  vendorCode,
  cartItems,
  lineItems
) => {
  if (!cartItems || !lineItems) return [];

  const taggedCartItems = [];
  for (const cartItem of cartItems) {
    const targetLineItems = lineItems.filter((lineItem) =>
      isMatchingLineItem(cartItem, lineItem, vendorCode)
    );

    if (targetLineItems.length === 0) {
      taggedCartItems.push({
        ...cartItem,
        invoiceNumber: "Unassigned Items",
      });
      continue;
    }

    for (const lineItem of targetLineItems) {
      taggedCartItems.push({
        ...cartItem,
        quantity: lineItem.quantity,
        invoiceNumber: lineItem.invoice_number,
        closest_warehouse: lineItem.closest_warehouse,
        lowest_price: lineItem.lowest_price,
        price_difference: lineItem.price_difference,
        totalPrice: lineItem.price * lineItem.quantity,
        master_brand: lineItem.master_brand,
        invoiceNickname: lineItem.invoice_nickname,
      });
    }
  }
  return taggedCartItems;
};

const cartsSlice = createSlice({
  name: "carts",
  initialState: {
    carts: {},
    showCartComposer: false,
  },
  reducers: {
    fetchCartsStartBatch(state, action) {
      const { vendorCodes, onlyIfIdle } = action.payload;
      const updatedCarts = {};
      const updatedVendors = [];

      // relevant when refreshing carts
      // Find any direct vendor carts (those not matching standard vendor codes)
      const directVendorCodes = Object.keys(state.carts)?.filter((code) => {
        return !Object.values(vendors).find((vendor) => vendor.code === code);
      });

      // Set a loading indicator for direct carts, but only if we need to fetch fresh data
      // This indicator will be used to show loading state and then removed when data arrives
      if (directVendorCodes.length === 0 && vendorCodes.includes("direct")) {
        updatedCarts["direct"] = {
          loading: LOADING_STATES.LOADING_STATUS,
          isDirectLoadingIndicator: true,
        };
      }

      const allVendorCodes = vendorCodes.concat(directVendorCodes);
      allVendorCodes.forEach((vendorCode) => {
        const cartLoadingStatus = state.carts?.[vendorCode]?.loading;
        if (
          (onlyIfIdle && cartLoadingStatus === LOADING_STATES.IDLE_STATUS) ||
          (!onlyIfIdle && cartLoadingStatus !== LOADING_STATES.LOADING_STATUS)
        ) {
          updatedCarts[vendorCode] = {
            ...state.carts?.[vendorCode],
            loading: LOADING_STATES.LOADING_STATUS,
            error: null,
          };
          updatedVendors.push(vendorCode);
        }
      });
      state.carts = {
        ...state.carts,
        ...updatedCarts,
      };
      action.payload.updatedVendors = updatedVendors;
    },

    fetchCartSuccess(state, action) {
      const { vendorCode, cartData } = action.payload;
      if (cartData?.items?.length === 0 || cartData?.totalPrice === 0) {
        delete state.carts[vendorCode];
        return;
      }
      state.carts[vendorCode] = {
        ...state.carts[vendorCode],
        loading: LOADING_STATES.SUCCESS_STATUS,
        error: null,
        totalPrice: cartData?.totalPrice ?? 0,
        items: cartData?.items ?? [],
        ...cartData,
      };
    },

    fetchDirectCartSuccess(state, action) {
      const { directCartData } = action.payload;
      // remove this temporary loading entry
      delete state.carts?.["direct"];

      if (!directCartData) return;

      // Define metadata fields to exclude
      const metadataFields = [
        "action",
        "error",
        "vendorCode",
        "message_id",
        "chunked",
        "total_chunks",
        "total_size",
      ];

      // Process each vendor cart in the direct cart data
      Object.entries(directCartData).forEach(([vendorCode, cartData]) => {
        // Skip metadata fields
        if (metadataFields.includes(vendorCode)) return;

        // Skip empty carts
        if (!cartData || Object.keys(cartData).length === 0) {
          delete state.carts[vendorCode];
          return;
        }

        // Update the cart in state using the same format as regular carts
        state.carts[vendorCode] = {
          ...state.carts[vendorCode],
          loading: LOADING_STATES.SUCCESS_STATUS,
          error: false,
          totalPrice: cartData?.totalPrice ?? 0,
          // Ensure freeShippingMin is passed through correctly from backend
          freeShippingMin: cartData?.freeShippingMin,
          ...cartData,
        };
      });
    },

    fetchCartFailure(state, action) {
      const { vendorCode, errorCode, errorMessage } = action.payload;
      state.carts[vendorCode] = {
        ...state.carts[vendorCode],
        loading: LOADING_STATES.FAILURE_STATUS,
        error: { [errorCode]: errorMessage },
        data: { items: [], totalPrice: 0.0 },
      };
    },

    // New action for synchronizing composed carts
    syncComposedCartsSuccess(state, action) {
      // Merge the new carts with existing carts instead of replacing
      state.carts = {
        ...state.carts, // Keep existing carts
        ...action.payload.carts, // Overlay with new/updated carts
      };
      state.showCartComposer = false;

      // Only update the status for carts that were in the payload
      Object.keys(action.payload.carts).forEach((vendorCode) => {
        if (state.carts[vendorCode]) {
          state.carts[vendorCode].loading = LOADING_STATES.SUCCESS_STATUS;
          state.carts[vendorCode].error = null;
        }
      });
    },

    // Add new reducer for switching cart composer view
    switchToCartComposer(state) {
      state.showCartComposer = true;
    },
    
    // Add reducer to set showCartComposer to any value
    setShowCartComposer(state, action) {
      state.showCartComposer = action.payload;
    },
  },
  selectors: {
    selectCart: (state, vendorCode) => {
      return state.carts?.[vendorCode];
    },
    selectCartRedirectUrl: (state, vendorCode) => {
      return state.carts?.[vendorCode]?.redirect_url;
    },
    selectCarts: (state, vendorCodes) => {
      const selectedCarts = {};
      for (const code of vendorCodes) {
        selectedCarts[code] = state.carts?.[code];
      }
      return selectedCarts;
    },
    selectDirectCarts: createSelector([(state) => state.carts], (carts) => {
      // still loading in direct cart data
      if (carts["direct"]) {
        return carts["direct"];
      }
      const directCarts = {};
      const allCarts = Object.entries(carts);
      for (const [vendorCode, cartData] of allCarts) {
        const isDirectVendor = !Object.values(vendors).find(
          (vendor) => vendor.code === vendorCode
        );
        if (isDirectVendor) {
          // Process the warehouse information if it exists
          const processedCartData = { ...cartData };

          // If warehouses information is available, ensure items have correct warehouse info
          if (
            processedCartData.warehouses &&
            Object.keys(processedCartData.warehouses).length > 0
          ) {
            // Ensure each item has its warehouse name properly set for display
            if (processedCartData.items) {
              processedCartData.items = processedCartData.items.map((item) => {
                const warehouseId = item.warehouse || "default";
                const warehouseData =
                  processedCartData.warehouses[warehouseId] || {};

                return {
                  ...item,
                  warehouseName:
                    warehouseData.name || item.warehouseName || warehouseId,
                };
              });
            }
          }

          directCarts[vendorCode] = processedCartData;
        }
      }
      return directCarts;
    }),

    // Add new selector for showCartComposer
    selectShowCartComposer: (state) => state.showCartComposer,
  },
});

export default cartsSlice.reducer;
export const {
  selectCart,
  selectCartRedirectUrl,
  selectCarts,
  selectDirectCarts,
  selectShowCartComposer,
} = cartsSlice.selectors;

export const {
  fetchCartsStartBatch,
  fetchCartSuccess,
  fetchCartFailure,
  fetchDirectCartSuccess,
  syncComposedCartsSuccess,
  switchToCartComposer,
  setShowCartComposer,
} = cartsSlice.actions;

export { cartsSlice };
